import { mapActions, mapGetters } from "vuex";
import { injectCssVars } from "@/scripts/common/utilities/cssVars.js";
import { fontStyleMapping, isCtaButtonFontStyleKey } from "@/scripts/common/utilities/textStyleHelpers.js";
import { preloadFontPair } from "@/scripts/settingsEditor/_helpers/fontPreload.js";
import { isTemplatePreviewRoute } from "./portfolioPreviewCache.js";
import { findPortfolioGenerationPreviewPage, isPortfolioGenerationPreviewRoute } from "@/scripts/view/portfolio/generationPreview.js";

// Shared by every page that can be shown inside the AI generator's preview iframe, so the
// palette and font controls keep working after the visitor switches to the case study.
export const templatePreviewMixin = {
    computed: {
        ...mapGetters("design", ["getAllCssVars", "fontStyles"]),
        ...mapGetters("view", ["viewData"])
    },
    mounted() {
        if (this.inGeneratorPreview()) {
            window.addEventListener("message", this.handleTemplatePreviewMessage);
            this.preloadPreviewFontPair();
            this.announcePreviewRoute();
        }
    },
    watch: {
        // Home, about and custom pages are separate components and announce on mount;
        // moving between case studies keeps the same one, so the route is watched too.
        "$route.fullPath"() {
            if (this.inGeneratorPreview()) this.announcePreviewRoute();
        }
    },
    unmounted() {
        window.removeEventListener("message", this.handleTemplatePreviewMessage);
    },
    methods: {
        ...mapActions("design", ["updatePreviewColorPalette", "applyColorPalette"]),
        // A generation's preview pages count however they were reached: the generator's
        // own links carry templatePreview, but the portfolio's links between its pages
        // drop it, and the page would otherwise stop listening once clicked through.
        inGeneratorPreview() {
            return isTemplatePreviewRoute(this.$route) || isPortfolioGenerationPreviewRoute(this.$route);
        },
        // Tells the generator which page is showing, so its page tabs follow a click on a
        // link inside the portfolio.
        announcePreviewRoute() {
            if (window.parent === window) return;
            window.parent.postMessage({
                type: "TEMPLATE_PREVIEW_NAVIGATED",
                payload: { path: this.$route.fullPath }
            }, window.location.origin);
        },
        preloadPreviewFontPair() {
            const headline = this.fontStyles?.heroTitle?.font;
            const text = this.fontStyles?.heroParagraph?.font;
            if (headline || text) {
                preloadFontPair({ headline, text });
            }
        },
        async handleTemplatePreviewMessage(event) {
            if (event.origin !== window.location.origin || event.source !== window.parent) return;

            // The generator's page tabs: moved through the portfolio's own router, exactly
            // as its links do, so the page switches in place instead of reloading.
            if (event.data?.type === "NAVIGATE_TEMPLATE_PREVIEW") {
                const path = event.data.payload?.path;
                if (typeof path !== "string" || !path.startsWith("/") || path.startsWith("//")) return;
                if (path !== this.$route.fullPath) {
                    this.$router.push(path).catch(() => {});
                }
                return;
            }

            if (
                event.data?.type === "UPDATE_TEMPLATE_PREVIEW"
                && isPortfolioGenerationPreviewRoute(this.$route)
            ) {
                await this.replaceTemplatePreview(event.data.payload);
                return;
            }

            if (
                event.data?.type === "UPDATE_PALETTE"
                && typeof event.data.payload?.ID === "string"
                && Array.isArray(event.data.payload?.COLORS)
            ) {
                await this.updatePreviewColorPalette({
                    id: event.data.payload.ID,
                    colors: event.data.payload.COLORS
                });
                await this.applyColorPalette();
                this.updateParentPreview();
            }

            if (
                event.data?.type === "UPDATE_FONT_PAIR"
                && event.data.payload
                && typeof event.data.payload === "object"
                && !Array.isArray(event.data.payload)
                && typeof event.data.payload._id === "string"
            ) {
                const selectedPair = event.data.payload;
                await preloadFontPair(selectedPair);
                const updatedFontStyles = Object.fromEntries(
                    Object.entries(this.fontStyles).map(([key, style]) => [
                        key,
                        {
                            ...style,
                            font: selectedPair[fontStyleMapping[key] || "text"],
                            ...(isCtaButtonFontStyleKey(key) ? { fontWeight: "400" } : {})
                        }
                    ])
                );
                this.$store.dispatch("design/updateFontStyles", updatedFontStyles);
                this.$store.dispatch("design/updateFontPairId", selectedPair._id);
                this.updateParentPreview();
            }
        },
        async replaceTemplatePreview(payload) {
            if (
                !payload?.portfolio
                || !payload?.project
                || !Array.isArray(payload.portfolioPages)
                || !Array.isArray(payload.caseStudies)
            ) return;

            const currentProjectId = this.$route.params.projectShortId;
            const project = currentProjectId
                ? findPortfolioGenerationPreviewPage(payload, currentProjectId)
                : payload.project;
            if (!project) return;

            const generationPreviewId = this.$route.params.generationId;
            this.$store.state.portfolio = payload.portfolio;
            this.$store.state.project = project;
            this.$store.commit("view/updateView", {
                ...this.viewData,
                portfolio: payload.portfolio,
                project,
                user: payload.user || this.viewData.user,
                portfolioPages: payload.portfolioPages,
                caseStudies: payload.caseStudies,
                activePage: project.shortId,
                portfolioTemplate: payload.portfolioTemplate || this.viewData.portfolioTemplate,
                generationPreviewId
            });
            this.$store.commit("view/updateActivePage", project.shortId);
            await this.loadPreviewTheme(payload.portfolio);
            this.preloadPreviewFontPair();
            this.updateParentPreview();
            // Once the new style has rendered, so the generator can bring it into focus.
            await this.$nextTick();
            if (window.parent !== window) {
                window.parent.postMessage({ type: "TEMPLATE_PREVIEW_UPDATED" }, window.location.origin);
            }
        },
        async loadPreviewTheme(portfolio) {
            const theme = portfolio?.templateSettings?.theme;
            if (!theme || theme.includes("profile_")) return;

            try {
                await import(`../../../styles/_${theme.replaceAll("_", "-").replace("profile_", "")}-base.scss`);
            } catch (error) {
                // The initial preview uses the same best-effort theme loading behavior.
            }
        },
        updateParentPreview() {
            const designSettings = this.viewData?.portfolio?.designSettings;
            if (designSettings) {
                window.parent.postMessage(
                    { type: "STORE_UPDATE", payload: JSON.parse(JSON.stringify(designSettings)) },
                    window.location.origin
                );
            }
            injectCssVars(this.getAllCssVars);
        }
    }
};

export default templatePreviewMixin;
